import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TranslateModule } from '@ngx-translate/core';
import { CdnUrlPipe } from '@shared/pipes';
import { ISearchResult } from '@core/models';
import { Router } from '@angular/router';
import { ExternalNavigation, Search } from '@core/services';
import { priceWithIva } from '@shared/utils/price';

/**
 * Component responsible for rendering a marker card with image, title, rating and click actions
 */
@Component({
  selector: 'app-marker-card',
  imports: [LazyLoadImageModule, TranslateModule, CdnUrlPipe],
  templateUrl: './marker-card.component.html',
  styleUrl: './marker-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarkerCard {
  /**
   * Router service for navigation
   */
  private readonly router = inject(Router);

  /**
   * Search service for managing marker selection
   */
  private readonly searchService = inject(Search);

  /** Browser-safe helper used to open VR links in a separate tab. */
  private readonly externalNavigation = inject(ExternalNavigation);

  /**
   * Input variable that receives the search result from the parent component
   */
  readonly marker = input.required<ISearchResult>();

  /**
   * Input variable that indicates if the card is displayed inside a carousel
   */
  readonly isCarousel = input<boolean>(false);

  /** Number of columns selected for the mobile search layout. */
  readonly mobileColumns = input<1 | 2>(2);

  /**
   * Input variable that determines whether to show the map button (now as floating location icon)
   */
  readonly showBtnMap = input<boolean>(true);

  /**
   * Emits when the user clicks the 360° view button in the floating action bar
   */
  readonly vrView = output<void>();

  /**
   * Emits when the user clicks the tour/walk button in the floating action bar
   */
  readonly tourView = output<void>();

  /**
   * Accessible label describing the card destination for screen readers
   */
  protected readonly cardAriaLabel = computed<string>(() => {
    const marker = this.marker();
    const markerName = marker?.language?.name ?? marker?.name ?? 'Geomarcador';
    const markerLocation = this.location();
    return markerLocation
      ? `Ver perfil de ${markerName} en ${markerLocation}`
      : `Ver perfil de ${markerName}`;
  });

  /**
   * Counter used to force reload when navigating to the map
   */
  private reload = 0;

  /**
   * Generates the complete geomarker location combining city and state
   */
  protected readonly location = computed<string>(() => {
    const city = this.marker()?.city;
    if (!city) return '';
    return `${city.language?.name} - ${city.state?.name}`;
  });

  /**
   * Gets the absolute value of the geomarker qualification
   */
  protected readonly qualification = computed<number>(() => {
    return Math.abs(this.marker()?.qualification || 0);
  });

  /** Información de precios calculada para la tarjeta. */
  protected readonly priceDetails = computed(() => {
    const pricing = this.marker()?.pricing;
    const baseAmount = this.toAmount(pricing?.base);
    const currentAmount = this.toAmount(pricing?.with_discount) ?? baseAmount;
    const hasPrice = currentAmount !== null && currentAmount > 0;
    const hasDiscount = hasPrice && baseAmount !== null && currentAmount < baseAmount;
    const savingsAmount = hasDiscount ? baseAmount - currentAmount : 0;
    const discountPercentage = hasDiscount ? (savingsAmount / baseAmount) * 100 : 0;
    const base = priceWithIva(baseAmount);
    const current = priceWithIva(currentAmount);
    const savings = priceWithIva(savingsAmount) ?? 0;

    return {
      base: base ?? null,
      current: current ?? null,
      savings,
      hasPrice,
      hasDiscount,
      isSuperOffer: discountPercentage > 30,
    };
  });

  /**
   * Navigates the user to the search map showing the selected geomarker
   */
  protected goToMap(): void {
    this.reload++;
    this.searchService.markerSelectedSubject.next(this.marker());
    this.router.navigate([`search/`], {
      queryParams: {
        map: 1,
        marker: this.marker().id,
        reload: this.reload,
      },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Navigates the user to the profile of the selected geomarker
   */
  protected goToMarker() {
    this.router.navigate([`geomarker/${this.marker().slug}`]);
  }

  /**
   * Navigates the user to the selected city profile
   */
  protected goToCity() {
    const arrayUrl = this.marker().city.links.api.split('/');
    const idCity = arrayUrl[arrayUrl.length - 2];
    this.router.navigate([`cities/${idCity}`]);
  }

  /**
   * Handles keyboard activation of the whole card (Enter or Space).
   * Prevents default scrolling on Space.
   */
  protected onCardKeydown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
      event.preventDefault();
      this.goToMarker();
    }
  }

  /**
   * Handles the 360° view floating button click. Emits an event for the parent to handle.
   */
  protected goToVR(): void {
    const url = this.marker().vr_video;
    if (!url) return;

    this.externalNavigation.openExternal(url);
    this.vrView.emit();
  }

  /**
   * Handles the tour/walk floating button click. Emits an event for the parent to handle.
   */
  protected goToTour(): void {
    const url = this.marker().vr_tour;
    if (!url) return;

    this.externalNavigation.openExternal(url);
    this.tourView.emit();
  }

  /** Formatea los valores en pesos colombianos sin decimales. */
  protected formatPrice(value: number | null): string {
    return value === null ? '' : `$ ${Math.round(value).toLocaleString('es-CO')}`;
  }

  private toAmount(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;

    const amount = typeof value === 'string' ? Number(value.replace(/[^\d.-]/g, '')) : value;
    return Number.isFinite(amount) ? amount : null;
  }
}
